import type { RefObject } from "react";
import { DOCUMENT_FILE_TYPES, EstimateFile } from "@/enums/estimate-file";
import { EstimateType } from "@/enums/estimate-type";
import { useUpdateEstimationDetails } from "@/hooks/mutation/estimates/useUpdateEstimationDetails";
import { GeneralDetails } from "./GeneralDetails/GeneralDetails";
import { LineSheets } from "./LineSheets";
import { Links } from "./Links";
import { Photos } from "./Photos";
import { Sheets } from "./Sheets";
import { ScopeNotes } from "./ScopeNotes";
import { useDetailsSectionData } from "./useDetailsSectionData";
import type { SelectedTPAFields } from "./useTPARules";
import { PdfPagesWithHighlights } from "../PdfPagesWithHighlights";
import { useEstimateDetail } from "@/hooks/useEstimateDetail";
import { AllAuditTrails } from "./AuditTrail/AllAuditTrails";
import { useShowInternalDebugInfo } from "@/hooks/useShowInternalDebugInfo";
import { useGetEstimateResponsiveNotePhotosQuery } from "@/hooks/queries/responsive-note-photos";

interface DetailsSectionContentProps {
  selectedTab: string | undefined;
  estimateId: string;
  selectedTPAFields: SelectedTPAFields;
  updateSelectedTPAField: (field: keyof SelectedTPAFields, value: string) => void;
  photoPreviewOverlayRef: RefObject<HTMLDivElement | null>;
}

export function DetailsSectionContent({ selectedTab, estimateId, selectedTPAFields, updateSelectedTPAField, photoPreviewOverlayRef }: DetailsSectionContentProps) {
  const { estimation, links, files, rightSidebarCurrentTab, lineItemDetail, fullPageImagePath, updateLinks } = useDetailsSectionData(
    estimateId!,
  );

  const { updateLineItem } = useEstimateDetail(estimateId!);

  const { updateEstimationDetails } = useUpdateEstimationDetails(estimateId!);

  const showInternalDebugInfo = useShowInternalDebugInfo();
  const {
    data: responsiveNotePhotos = [],
    isLoading: isResponsiveNotePhotosLoading,
    isError: hasResponsiveNotePhotosError,
  } = useGetEstimateResponsiveNotePhotosQuery(estimateId);

  const photos = files.filter((file) => file.fileType === EstimateFile.IMAGE);

  switch (selectedTab) {
    case "Details":
      return (
        <GeneralDetails
          estimateId={estimateId!}
          estimation={estimation}
          updateEstimationDetails={updateEstimationDetails}
          selectedTPAFields={selectedTPAFields}
          updateSelectedTPAField={updateSelectedTPAField}
        />
      );
    case "Photos":
      return (
        <Photos
          estimateId={estimateId}
          estimation={estimation}
          files={photos}
          responsiveNotePhotos={responsiveNotePhotos}
          isResponsiveNotePhotosLoading={isResponsiveNotePhotosLoading}
          hasResponsiveNotePhotosError={hasResponsiveNotePhotosError}
          photoPreviewOverlayRef={photoPreviewOverlayRef}
          canExpand
        />
      );
    case "Notes":
      return <ScopeNotes estimateId={estimateId} showHeader={true} />;
    case "Links":
      return <Links updateLinks={updateLinks} links={links} />;
    case "Documents": {
      if (estimation?.pdfPagesAsImages?.length) {
        return (
          <div className="flex h-full w-full flex-col overflow-hidden">
            <PdfPagesWithHighlights pdfPagesAsImages={estimation.pdfPagesAsImages} showWithoutLineItem={true} fillContainer={true} />
          </div>
        );
      }
      const docs = files.filter((file) => DOCUMENT_FILE_TYPES.includes(file.fileType as EstimateFile));
      return <Sheets files={docs} />;
    }
    case "Audit Trail":
      return showInternalDebugInfo ? (
        <AllAuditTrails />
      ) : (
        // we shouldn't hit this state since we won't include the audit trail tab in the list of
        // available tabs for users for whom the audit trail is disabled
        <>Audit Trail is Currently Not Enabled</>
      );
    case "Item":
      if (lineItemDetail) {
        return (
          <LineSheets
            estimateId={estimateId}
            hideHeader
            pageImage={fullPageImagePath}
            region={lineItemDetail.tuple.tupleRegion}
            category={rightSidebarCurrentTab?.category}
            isTimeAndMaterial={estimation?.estimateType === EstimateType.TIME_AND_MATERIAL}
            updateLineItemComment={(pageId, tupleId, lineItemId, lineItemUniqueId, comment) =>
              updateLineItem(
                pageId,
                tupleId,
                lineItemId,
                lineItemUniqueId,
                undefined,
                undefined,
                undefined,
                undefined,
                true,
                undefined,
                undefined,
                undefined,
                comment,
              )
            }
            files={files.filter((file) => file.fileType === EstimateFile.TIC)}
          />
        );
      }
      return null;
    default:
      return null;
  }
}
